import React from 'react'
import Ink from 'react-ink'
import MediaQuery from 'react-responsive'

import { datadogRum } from '@datadog/browser-rum'
import { find, propEq } from 'ramda'
import { compose, withHandlers, withState } from 'recompose'
import styled from 'styled-components'

import CheckPaddedURL from 'assets/icons/check-padded.svg'
import downArrow from 'assets/icons/down-arrow.svg'
import withClickOutside from 'components/HOCs/withClickOutside'
import theme from 'theme'
import { DROPDOWN_OVERLAY } from 'utils/testing/testIDs'

type Props = {
  children?: React.ReactNode
  label?: string
  options: Option[]
  onChange: (value: string) => void
  onOpen?: () => void
  onClose?: () => void
  selected?: string
  shadow?: boolean
}

type StateProps = {
  isOpen: boolean
  setIsOpen: (val: boolean) => void
  selected: string
}

type HandlerProps = {
  handleClick: (event: any) => void
  toggleDropDown: () => void
  closeDropdown: () => void
  openDropdown: () => void
}

type InnerProps = Props & StateProps & HandlerProps

export type Option = {
  label: string
  value: string
  isCancelOption?: boolean
  dropDownLabel?: string
}

interface IDropdownOptions {
  handleClick: (event: any) => void
  selected: string
  options: Option[]
}

interface IDropdownItem {
  handleClick: (event: any) => void
  option: Option
  selected: string
}

const Container = styled.div`
  display: block;
  position: relative;
`

const ButtonContainer = styled('button')<{
  shadow?: boolean
  disabled?: boolean
}>`
  align-items: center;
  background-color: ${theme.colors.white};
  border: solid 1px ${theme.colors.stoneLight};
  border-radius: 99px;
  display: flex;
  height: 44px;
  justify-content: center;
  min-width: 148px;
  margin: 0px;
  padding: 0 22px;
  position: relative;
  width: 100%;

  ${(props) =>
    props.shadow &&
    `border: solid 1px ${theme.colors.white}; box-shadow: ${theme.shadows.smDark};`};

  @media screen and (max-width: ${theme.breakpoints.md}px) {
    margin: 0 12px 0 0;
  }

  @media screen and (max-width: ${theme.breakpoints.sm}px) {
    min-width: 100%;
    margin-left: 0;
  }

  &:hover {
    background: ${theme.colors.ivoryLight};
  }

  ${(props) => (props.disabled ? 'cursor: not-allowed;' : 'cursor: pointer;')};
`
const Label = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.slate};
  margin: 0 auto;
`
const Arrow = styled.div`
  width: 10px;
  height: 10px;
  background: url('${downArrow}') no-repeat center center;
  margin-left: 9px;
`

const DropdownContainer = styled.div`
  position: absolute;
  background: ${theme.colors.white};
  top: 60px;
  left: 50%;
  transform: translateX(-50%);
  width: 200px;
  border-radius: ${theme.styles.borderRadius};
  box-shadow: ${theme.shadows.lgMedium};
  margin-top: -8px;

  &::after {
    content: '';
    position: absolute;
    top: -7px;
    left: 96px;
    margin: 0 0 0 -6px;
    width: 20px;
    height: 20px;
    transform: rotate(45deg);
    border-radius: 2px;
    background: #fff;
    will-change: transform;
    transition-property: transform;
    transition-duration: 0.25s;
  }

  @media screen and (max-width: ${theme.breakpoints.md}px) {
    bottom: 20px;
    position: fixed;
    top: auto;
    width: calc(100% - 40px);
    z-index: 9999;

    &::after {
      display: none;
    }
  }

  p {
    font-size: 14px;
    color: ${theme.colors.stone};
    background: #fff;
  }

  ul {
    margin: 0;
    padding: 0;
  }

  li {
    align-items: center;
    border-bottom: 1px solid ${theme.colors.ivory};
    display: flex;
    justify-content: space-between;
    list-style-type: none;
    width: 100%;
    cursor: pointer;

    &:last-child {
      border-bottom: none;
    }
  }
`

const IconDropdownContainer = styled(DropdownContainer)`
  @media (min-width: ${theme.breakpoints.md}px) {
    left: 50%;
    transform: translateX(-50%);
    top: 40px;
    z-index: 9999;
  }
`

const Overlay = styled.div`
  background-color: ${theme.colors.slate};
  bottom: 0;
  left: 0;
  opacity: 0.6;
  position: fixed;
  right: 0;
  top: 70px;
  z-index: 10;
`

const DropdownOption = styled.h1<{ isCancelOption: boolean }>`
  color: ${theme.colors.slate};
  font-size: 14px;
  font-weight: normal;
  margin: 0;
  padding: 20px;

  @media (max-width: ${theme.breakpoints.md}px) {
    color: ${(props) =>
      props.isCancelOption ? theme.colors.slate : theme.colors.green};
    font-weight: 600;
    width: 100%;
    text-align: center;
  }
`

const getSelectedOption = (props: Props): Option => {
  return find(propEq('value', props.selected))(props.options) as Option
}

const getSelectedLabel = (props: Props) => {
  const selectedOption = getSelectedOption(props)
  return selectedOption
    ? selectedOption.label || selectedOption.dropDownLabel
    : ''
}

const DropdownItem = (props: IDropdownItem) => {
  try {
    const { option } = props

    if (!option) {
      throw new Error('option on Dropdown Item does not exist')
    }
    if (!option?.value) {
      throw new Error('option value on Dropdown Item does not exist')
    }
    if (!option?.dropDownLabel && !option?.label) {
      throw new Error('option label on Dropdown Item does not exist')
    }

    if (option.isCancelOption) {
      return (
        <MediaQuery maxWidth={theme.breakpoints.md}>
          <li onClick={props.handleClick} data-key={option.value} role="button">
            <DropdownOption isCancelOption={option.isCancelOption}>
              {option.dropDownLabel || option.label}
            </DropdownOption>
            {option.value === props.selected && (
              <img src={CheckPaddedURL} alt="Check" />
            )}
          </li>
        </MediaQuery>
      )
    } else {
      return (
        <li onClick={props.handleClick} data-key={option.value} role="button">
          <DropdownOption isCancelOption={Boolean(option.isCancelOption)}>
            {option.dropDownLabel || option.label}
          </DropdownOption>
          {option.value === props.selected && (
            <img src={CheckPaddedURL} alt="Check" />
          )}
        </li>
      )
    }
  } catch (error) {
    datadogRum.addError(error)
  }
  return <></>
}

const DropdownOptions = (props: IDropdownOptions) => (
  <ul>
    {Object.entries(props.options).map(([key, option]) => (
      <DropdownItem
        key={key}
        handleClick={props.handleClick}
        option={option}
        selected={props.selected}
      />
    ))}
  </ul>
)

const enhance = compose<InnerProps, Props>(
  withState('isOpen', 'setIsOpen', false),
  withHandlers<
    StateProps & Props,
    Pick<HandlerProps, 'closeDropdown' | 'openDropdown'>
  >({
    closeDropdown:
      ({ setIsOpen, onClose }) =>
      () => {
        setIsOpen(false)

        if (onClose !== undefined) {
          onClose()
        }
      },
    openDropdown:
      ({ setIsOpen, onOpen }) =>
      () => {
        setIsOpen(true)

        if (onOpen !== undefined) {
          onOpen()
        }
      },
  }),
  withHandlers<
    StateProps & Props & Pick<HandlerProps, 'closeDropdown' | 'openDropdown'>,
    Pick<HandlerProps, 'handleClick' | 'toggleDropDown'>
  >({
    handleClick:
      ({ onChange, closeDropdown }) =>
      (event) => {
        const selectedView = event.currentTarget.getAttribute('data-key')
        onChange(selectedView)
        closeDropdown()
      },
    toggleDropDown:
      ({ isOpen, closeDropdown, openDropdown }) =>
      () => {
        isOpen ? closeDropdown() : openDropdown()
      },
  }),
  withClickOutside(({ closeDropdown, isOpen }: StateProps & HandlerProps) => {
    if (isOpen) {
      closeDropdown()
    }
  }),
)

export const IconDropdownMenu = enhance((props) => (
  <Container>
    <div onClick={props.toggleDropDown}>{props.children}</div>
    {props.isOpen && (
      <>
        <MediaQuery maxWidth={theme.breakpoints.md}>
          <Overlay
            data-testid={DROPDOWN_OVERLAY}
            onClick={props.closeDropdown}
          />
        </MediaQuery>
        <IconDropdownContainer className="icon-dropdown-container">
          <DropdownOptions
            handleClick={props.handleClick}
            options={props.options}
            selected={props.selected}
          />
        </IconDropdownContainer>
      </>
    )}
  </Container>
))

export default enhance((props) => (
  <Container>
    <ButtonContainer
      onClick={props.toggleDropDown}
      data-testid={'dropdown'}
      shadow={props.shadow}
    >
      <Ink opacity={0.02} />
      <Label>{props.label || getSelectedLabel(props)}</Label>
      <Arrow />
    </ButtonContainer>

    {props.isOpen && (
      <>
        <MediaQuery maxWidth={theme.breakpoints.md}>
          <Overlay
            data-testid={DROPDOWN_OVERLAY}
            onClick={props.closeDropdown}
          />
        </MediaQuery>
        <DropdownContainer>
          <DropdownOptions
            handleClick={props.handleClick}
            options={props.options}
            selected={props.selected}
          />
        </DropdownContainer>
      </>
    )}
  </Container>
))
